import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { StudioColumnMetadata, StudioTableMetadata } from "modules/studio/services/studio-api";

export const systemFields = new Set(["id", "number_ref", "created_at", "updated_at"]);

export const stringifyFieldValue = (value: unknown, column?: StudioColumnMetadata | null) => {
    if (value == null) {
        return "";
    }

    if (column?.column_type === "CODE" && column.column_subtype === "JSON") {
        if (typeof value === "string") {
            try {
                return JSON.stringify(JSON.parse(value), null, 2);
            } catch (_error) {
                return value;
            }
        }

        return JSON.stringify(value, null, 2);
    }

    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
    }

    return String(value);
};

export const parseFieldValue = (value: string, column?: StudioColumnMetadata | null) => {
    if (!column) {
        return value;
    }

    if (column.column_type === "BOOLEAN") {
        return ["true", "1", "yes", "sim"].includes(value.trim().toLocaleLowerCase());
    }

    if (column.column_type === "INTEGER") {
        return value.trim() === "" ? null : Number.parseInt(value, 10);
    }

    if (["DECIMAL", "CURRENCY"].includes(column.column_type)) {
        return value.trim() === "" ? null : Number.parseFloat(value);
    }

    if (column.column_type === "CODE" && column.column_subtype === "JSON") {
        return value.trim() ? JSON.parse(value) : null;
    }

    return value;
};

export const toSysTable = (table: StudioTableMetadata) => new SysTable({
    id: table.id,
    label: table.label,
    name: table.name,
    number_ref: table.number_ref || "",
    created_at: new Date(),
    updated_at: new Date(),
});

export const toSysColumn = (column: StudioColumnMetadata) => new SysColumn({
    id: column.id,
    sys_table_id: column.sys_table_id || "",
    active: column.active ?? true,
    label: column.label,
    name: column.name,
    column_type: column.column_type,
    column_subtype: column.column_subtype || "",
    order: column.order || 0,
    referenced_table_name: column.referenced_table_name || "",
    reference_filter: "",
    currency_code: "",
    currency_decimal_places: null,
    default_value: column.default_value || "",
    is_display: column.is_display || false,
    is_hidden: column.is_hidden || false,
    is_mandatory: column.is_mandatory || false,
    depends_on_column_id: "",
    created_at: new Date(),
    updated_at: new Date(),
});

export const packageChangeTableName = (resourceKey?: string | null) => {
    if (!resourceKey) {
        return null;
    }

    const [tableName] = String(resourceKey).split(/[.:]/);
    return tableName || null;
};

export const setToArray = (value: Set<string>) => Array.from(value).sort();

export const normalizePreferenceSet = (value: unknown) =>
    Array.isArray(value)
        ? value.filter((item): item is string => typeof item === "string")
        : [];

export const safeRecordJson = (record: Record<string, any> | null | undefined) =>
    JSON.stringify(record ?? {}, null, 2);

export const isRequestCanceled = (error: any) =>
    error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.message === "canceled";

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const safeParseJson = <T,>(value: unknown, fallback: T): T => {
    if (Array.isArray(value) || (value && typeof value === "object")) {
        return value as T;
    }

    if (typeof value !== "string" || !value.trim()) {
        return fallback;
    }

    try {
        return JSON.parse(value) as T;
    } catch (_error) {
        return fallback;
    }
};
